import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Search, Container } from 'semantic-ui-react';
import { Media } from '../../mediaSetting';
import Avatar from 'react-avatar';
import _ from 'lodash';

import {
  getSearchUserSuggestions,
  clearSearchUserSuggestions,
  selectUserFromSearchSuggestions,
  removeUserFromSelectedUsers,
  selectSingleUserFromSearchSuggestions,
} from 'actions/user';
import { getUserAvatarText } from 'businessLogic/userAvatar';

export const customResultRenderer = ({ id, firstName, lastName, email, userImageUrl, isEmailOnlyUser }) => {
  const suggestionPrimaryText = isEmailOnlyUser ? email : `${firstName} ${lastName}`;
  const suggestionSecondaryText = isEmailOnlyUser ? '' : email;
  const avatarText = getUserAvatarText({
    userImageUrl,
    isEmailOnlyUser,
    firstName,
    lastName,
  });
  const avatarColor = isEmailOnlyUser ? window.styleVariable.primaryHoverColour : '';
  const avatarImageSrc = userImageUrl || '';
  const avatarTextSizeRatio = 2.5;

  return (
    <div className="search-user-suggestion" key={email}>
      <Container as={Media} greaterThanOrEqual="computer" className="auto-width fl">
        <Avatar
          round={true}
          size={30}
          name={avatarText}
          src={avatarImageSrc}
          className="user-avatar"
          color={avatarColor}
          textSizeRatio={avatarTextSizeRatio}
        />
      </Container>
      <Container as={Media} lessThan="computer" className="auto-width fl">
        <Avatar
          size={50}
          round={true}
          name={avatarText}
          src={avatarImageSrc}
          className="user-avatar"
          color={avatarColor}
          textSizeRatio={avatarTextSizeRatio}
          style={{ marginTop: '-2px' }}
        />
      </Container>
      <div
        className={`fl pdlm pdtsss suggestion-primary-text ${
          !suggestionSecondaryText && 'suggestion-primary-text-fullwidth'
        }`}
      >
        {suggestionPrimaryText}
      </div>
      {suggestionSecondaryText && (
        <div className="fr pdlm pdtsss text-size-ms secondary-text suggestion-secondary-text">
          {suggestionSecondaryText}
        </div>
      )}
    </div>
  );
};
customResultRenderer.propTypes = {
  id: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  userImageUrl: PropTypes.string,
};

export default function SearchSelectUsers(props) {
  const dispatch = useDispatch();
  const searchResults = useSelector((state) => state.usersReducer.searchSelectUsersForm.searchResults);
  const isLoading = useSelector((state) => state.usersReducer.searchSelectUsersForm.isLoading);
  const selectedUsers = useSelector((state) => state.usersReducer.searchSelectUsersForm.selectedUsers);
  const searchTerm = useSelector((state) => state.usersReducer.searchSelectUsersForm.searchTerm);

  const handleSearchChange = (e, { value }) => {
    if (
      !props.allowMultipleUserSelection &&
      selectedUsers.length === 1 &&
      selectedUsers[0].email.toLowerCase() === value.toLowerCase()
    ) {
      // user already selection while component is in single selection mode
      // and searchterm is matching selected user,
      // hence no search required
      return;
    }

    if (!props.allowMultipleUserSelection && selectedUsers.length === 1) {
      // user already selected while component is in single selection mode
      // and searchterm is NOT matching selected user,
      // then clear the selection
      dispatch(removeUserFromSelectedUsers(selectedUsers[0]));
    }

    dispatch(getSearchUserSuggestions(value));
  };

  // Note - handleResultSelect event is not being called when using keyboard enter key for selection
  // TODO - Check if this keyboard support issue is fixed in this new verison of SemanticUI
  const handleResultSelect = (e, { result }) => {
    console.log('handleResultSelect called ', result);
    if (props.allowMultipleUserSelection) dispatch(selectUserFromSearchSuggestions(result));
    else dispatch(selectSingleUserFromSearchSuggestions(result));
    if (!props.notClearSearchAfterSelection) {
      // Clear search result
      dispatch(clearSearchUserSuggestions());
    }

    if (props.userSelectedCallBack) {
      props.userSelectedCallBack(result);
    }
  };

  const handleUserUnSelect = (userToRemove) => {
    console.log('handleUserUnSelect called ', userToRemove);
    dispatch(removeUserFromSelectedUsers(userToRemove));
  };

  const getSelectedUserMarkup = (user) => {
    const userBadgeText = user.isEmailOnlyUser ? user.email : `${user.firstName} ${user.lastName}`;
    const avatarText = getUserAvatarText(user);
    const avatarColor = user.isEmailOnlyUser ? window.styleVariable.primaryHoverColour : '';
    const avatarImageSrc = user.userImageUrl || '';
    const avatarTextSizeRatio = 2;

    return (
      <div className="ui image label mrtsss mrbxxs mrlnull mrrxs selected-user-badge" key={user.email}>
        <Avatar
          size={20}
          name={avatarText}
          src={avatarImageSrc}
          className="fl user-avatar"
          color={avatarColor}
          textSizeRatio={avatarTextSizeRatio}
        />
        <span className={`fl info-color ${userBadgeText.length > 30 && 'trim-long-text user-badge-text'}`}>
          {userBadgeText}
        </span>
        <i className="fr delete icon pdtsss" onClick={() => handleUserUnSelect(user)} />
      </div>
    );
  };

  const selectedUsersMarkup = selectedUsers.length > 0 ? selectedUsers.map((user) => getSelectedUserMarkup(user)) : '';

  // exclude already selected users from search suggestions
  const maxSuggestionsToShow = 5;
  const searchUserSuggestions = searchResults
    .filter((suggestedUser) => {
      const suggestUserSelected = selectedUsers.find((selectedUser) => selectedUser.email === suggestedUser.email);
      return suggestUserSelected === undefined;
    })
    .slice(0, maxSuggestionsToShow);

  return (
    <div className="ui grid search-select-users">
      {props.allowMultipleUserSelection && selectedUsers.length > 0 && (
        <div className="sixteen wide column pdtnull pdbnull">
          <span className="placeholder-label color-placeholder">
            {APP.LANGUAGE.SearchSelectComponent.SelectedUsersFieldLabel}
          </span>
        </div>
      )}
      {props.allowMultipleUserSelection && selectedUsers.length > 0 && (
        <div className="sixteen wide column pdtnull pdbm selected-users">{selectedUsersMarkup}</div>
      )}
      <span className="sixteen wide column pdtnull pdbsss">
        <div className="placeholder-label color-placeholder">
          {props.searchFieldLabel ||
            APP.LANGUAGE.SearchSelectComponent.SearchBoxFieldLabel.replace('##entityType##', props.entityType)}
        </div>
      </span>
      <div className="sixteen wide column pdtnull">
        <Search
          loading={isLoading}
          onResultSelect={handleResultSelect}
          onSearchChange={handleSearchChange}
          results={searchUserSuggestions}
          value={searchTerm}
          resultRenderer={customResultRenderer}
          showNoResults={false}
          className="search-user"
          fluid
        />
      </div>
    </div>
  );
}

PropTypes.defaultProps = {
  allowNewInvite: false,
  entityType: 'Waiting Area',
};

PropTypes.propTypes = {
  allowNewInvite: PropTypes.bool, // If true, user can select new users via email (for invitation purposes)
  entityType: PropTypes.string,
  allowMultipleUserSelection: PropTypes.bool.isRequired,
  searchFieldLabel: PropTypes.string,
  userSelectedCallBack: PropTypes.func, //This is the custom call back hook which will be invoked when a user is selected.
};
